// Màn hình "Review & CSAT" — review tốt/xấu 3 kênh (Google, Zalo Mini App, Chat)
// + tỉ lệ chuyển đổi review (GR) từ staging ops-quality, kèm danh sách review
// xấu trong tháng để đọc nội dung. View-only.
import { useState } from 'react';
import { KpiCard } from '../components/KpiCard';
import { Tag } from '../components/Tag';
import { ChartBox, chartDataset } from '../components/ChartBox';
import type { OpsReviewsResponse } from '../types';
import { asNumber, monthLabel, num, pct1, uniq } from '../utils';

interface OpsReviewsScreenProps {
  data: OpsReviewsResponse | null;
  loaded: boolean;
  busy: boolean;
  month: string;
  onMonth: (month: string) => void;
}

const csatTone = (v: number): 'green' | 'amber' | 'red' => (v >= 95 ? 'green' : v >= 90 ? 'amber' : 'red');
const grTone = (v: number): 'green' | 'amber' | 'red' => (v >= 15 ? 'green' : v >= 10 ? 'amber' : 'red');

export function OpsReviewsScreen({ data, loaded, busy, month, onMonth }: OpsReviewsScreenProps) {
  const [badSource, setBadSource] = useState<'all' | 'google' | 'zalo'>('all');

  if (busy && !loaded) return <div className="card empty">Đang tải dữ liệu review...</div>;
  if (!loaded) return <div className="card empty">Mở mục Review &amp; CSAT để tải dữ liệu.</div>;

  const months = data?.meta?.months || [];
  const csatDaily = data?.csatDaily || [];
  const grDaily = data?.grDaily || [];
  const csatMonthly = data?.csatMonthly || [];
  const grMonthly = data?.grMonthly || [];
  const googleBad = data?.googleBad || [];
  const zaloBad = data?.zaloBad || [];

  const sum = (rows: Array<Record<string, unknown>>, key: string) => rows.reduce((a, r) => a + asNumber(r[key]), 0);
  const tot = sum(csatDaily, 'google_tot') + sum(csatDaily, 'zalo_tot');
  const xau = sum(csatDaily, 'google_xau') + sum(csatDaily, 'zalo_xau') + sum(csatDaily, 'chat_xau');
  const csatPct = tot + xau ? (100 * tot) / (tot + xau) : 0;
  const grTot = sum(grDaily, 'review_tot');
  const grDon = sum(grDaily, 'don_tao');
  const grPct = grDon ? (100 * grTot) / grDon : 0;

  // Daily trend trong tháng: tốt (stack google+zalo) vs xấu.
  const days = uniq(csatDaily.map(r => r.date));
  const byDay = (key: 'google_tot' | 'zalo_tot' | 'google_xau' | 'zalo_xau' | 'chat_xau') =>
    days.map(d => csatDaily.filter(r => r.date === d).reduce((a, r) => a + asNumber(r[key]), 0));
  const dailySets = [
    { ...chartDataset('Google tốt', byDay('google_tot'), '#2563eb'), stack: 'tot' },
    { ...chartDataset('Zalo tốt', byDay('zalo_tot'), '#0891b2'), stack: 'tot' },
    chartDataset('Xấu (3 kênh)', days.map((d, i) => byDay('google_xau')[i] + byDay('zalo_xau')[i] + byDay('chat_xau')[i]), '#d71920', 'line'),
  ];

  // Monthly: CSAT% và GR% theo tháng (toàn hệ thống).
  const yms = uniq([...csatMonthly.map(r => r.ym), ...grMonthly.map(r => r.ym)]);
  const monthlyCsat = yms.map(ym => {
    const rows = csatMonthly.filter(r => r.ym === ym);
    const t = sum(rows, 'google_tot') + sum(rows, 'zalo_tot');
    const x = sum(rows, 'google_xau') + sum(rows, 'zalo_xau') + sum(rows, 'chat_xau');
    return t + x ? Math.round((1000 * t) / (t + x)) / 10 : NaN;
  });
  const monthlyGr = yms.map(ym => {
    const rows = grMonthly.filter(r => r.ym === ym);
    const t = sum(rows, 'review_tot');
    const d = sum(rows, 'don_tao');
    return d ? Math.round((1000 * t) / d) / 10 : NaN;
  });
  const monthlySets = [
    chartDataset('CSAT %', monthlyCsat, '#059669', 'line'),
    chartDataset('GR %', monthlyGr, '#7c3aed', 'line', 'y1'),
  ];

  // Bảng theo store trong tháng.
  const stores = uniq([...csatDaily.map(r => r.store_code), ...grDaily.map(r => r.store_code)]);
  const storeRows = stores.map(code => {
    const c = csatDaily.filter(r => r.store_code === code);
    const g = grDaily.filter(r => r.store_code === code);
    const gTot = sum(c, 'google_tot'), gXau = sum(c, 'google_xau');
    const zTot = sum(c, 'zalo_tot'), zXau = sum(c, 'zalo_xau');
    const chXau = sum(c, 'chat_xau');
    const t = gTot + zTot, x = gXau + zXau + chXau;
    const rTot = sum(g, 'review_tot'), don = sum(g, 'don_tao');
    return {
      code, gTot, gXau, zTot, zXau, chXau,
      csat: t + x ? (100 * t) / (t + x) : null,
      rTot, don, gr: don ? (100 * rTot) / don : null,
    };
  }).sort((a, b) => (b.gTot + b.zTot) - (a.gTot + a.zTot));

  const badRows = [
    ...(badSource !== 'zalo' ? googleBad.map(r => ({
      key: `g-${r.review_id}`, source: 'Google', store: r.store_code || '-', star: `${r.star_rating}★`,
      who: r.reviewer_display_name || '-', comment: r.comment, day: r.day || '-',
      extra: r.replied ? 'Đã phản hồi' : 'Chưa phản hồi', extraTone: (r.replied ? 'green' : 'red') as 'green' | 'red',
    })) : []),
    ...(badSource !== 'google' ? zaloBad.map(r => ({
      key: `z-${r.review_id}`, source: 'Zalo', store: r.store_code || '-',
      star: r.min_rating != null ? `${r.min_rating}★ min` : r.overall_rating != null ? `${r.overall_rating}★` : '-',
      who: r.employee_name || '-', comment: r.comment, day: r.day || '-',
      extra: asNumber(r.is_complaint) ? 'Khiếu nại' : 'Điểm thấp', extraTone: (asNumber(r.is_complaint) ? 'red' : 'amber') as 'red' | 'amber',
    })) : []),
  ].sort((a, b) => String(b.day).localeCompare(String(a.day)));

  return (
    <>
      <section className="filters">
        <div className="actions start">
          <span className="kpi-label">Tháng</span>
          {months.map(m => (
            <button key={m} className={`tab ${month === m ? 'active' : ''}`} onClick={() => onMonth(m)}>{monthLabel(m)}</button>
          ))}
        </div>
      </section>

      <section className="grid kpis">
        <KpiCard label="Review tốt" value={num(tot)} sub={`Google ${num(sum(csatDaily, 'google_tot'))} · Zalo ${num(sum(csatDaily, 'zalo_tot'))}`} tone="green" />
        <KpiCard label="Review / phản hồi xấu" value={num(xau)} sub={`Google ${num(sum(csatDaily, 'google_xau'))} · Zalo ${num(sum(csatDaily, 'zalo_xau'))} · Chat ${num(sum(csatDaily, 'chat_xau'))}`} tone={xau ? 'red' : 'green'} />
        <KpiCard label="CSAT" value={pct1(csatPct)} sub="tốt ÷ (tốt + xấu) · target 95%" tone={csatTone(csatPct)} />
        <KpiCard label="Chuyển đổi review (GR)" value={pct1(grPct)} sub={`${num(grTot)} review tốt / ${num(grDon)} đơn tạo · target 15%`} tone={grTone(grPct)} />
      </section>

      <section className="grid split atop space">
        <div className="card">
          <div className="section-title">
            <h2>Review theo ngày · {monthLabel(month)}</h2>
            <span>Cột = review tốt (Google + Zalo) · đường = xấu 3 kênh</span>
          </div>
          {days.length === 0 ? (
            <div className="card empty">Chưa có dữ liệu ngày cho {monthLabel(month)}.</div>
          ) : (
            <div className="chart"><ChartBox labels={days.map(d => d.slice(8))} datasets={dailySets} options={{ scales: { x: { stacked: true, grid: { display: false } }, y: { stacked: true, beginAtZero: true } } }} /></div>
          )}
        </div>
        <div className="card">
          <div className="section-title">
            <h2>CSAT &amp; GR theo tháng</h2>
            <span>Toàn hệ thống · từ 04/2026</span>
          </div>
          <div className="chart"><ChartBox type="line" labels={yms.map(monthLabel)} datasets={monthlySets} options={{ scales: { y: { beginAtZero: false, suggestedMax: 100 }, y1: { position: 'right', beginAtZero: true, grid: { display: false } } } }} /></div>
        </div>
      </section>

      <section className="card space">
        <div className="section-title">
          <h2>Theo cửa hàng · {monthLabel(month)}</h2>
          <span>CSAT = tốt ÷ (tốt + xấu) · GR = review tốt ÷ đơn tạo</span>
        </div>
        <div className="scroll">
          <table>
            <thead><tr>
              <th>Cửa hàng</th><th className="right">Google tốt</th><th className="right">Google xấu</th>
              <th className="right">Zalo tốt</th><th className="right">Zalo xấu</th><th className="right">Chat xấu</th>
              <th className="right">CSAT</th><th className="right">Review tốt</th><th className="right">Đơn tạo</th><th className="right">GR</th>
            </tr></thead>
            <tbody>{storeRows.map(r => (
              <tr key={r.code}>
                <td><b>{r.code}</b></td>
                <td className="right tnum">{num(r.gTot)}</td>
                <td className="right tnum">{r.gXau ? <Tag tone="red">{num(r.gXau)}</Tag> : <span className="muted">0</span>}</td>
                <td className="right tnum">{num(r.zTot)}</td>
                <td className="right tnum">{r.zXau ? <Tag tone="red">{num(r.zXau)}</Tag> : <span className="muted">0</span>}</td>
                <td className="right tnum">{r.chXau ? <Tag tone="red">{num(r.chXau)}</Tag> : <span className="muted">0</span>}</td>
                <td className="right">{r.csat == null ? <span className="muted">-</span> : <Tag tone={csatTone(r.csat)}>{pct1(r.csat)}</Tag>}</td>
                <td className="right tnum">{num(r.rTot)}</td>
                <td className="right tnum muted">{num(r.don)}</td>
                <td className="right">{r.gr == null ? <span className="muted">-</span> : <Tag tone={grTone(r.gr)}>{pct1(r.gr)}</Tag>}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </section>

      <section className="card">
        <div className="section-title">
          <h2>Review xấu cần đọc · {monthLabel(month)}</h2>
          <span>{num(badRows.length)} review · Google ≤3★ + Zalo khiếu nại/điểm thấp</span>
        </div>
        <div className="actions start">
          <span className="kpi-label">Kênh</span>
          <button className={`tab ${badSource === 'all' ? 'active' : ''}`} onClick={() => setBadSource('all')}>Tất cả</button>
          <button className={`tab ${badSource === 'google' ? 'active' : ''}`} onClick={() => setBadSource('google')}>Google</button>
          <button className={`tab ${badSource === 'zalo' ? 'active' : ''}`} onClick={() => setBadSource('zalo')}>Zalo</button>
        </div>
        {badRows.length === 0 ? (
          <div className="card empty">Không có review xấu trong {monthLabel(month)}. 🎉</div>
        ) : (
          <div className="scroll tall">
            <table>
              <thead><tr>
                <th>Ngày</th><th>Kênh</th><th>Cửa hàng</th><th className="right">Điểm</th><th>Khách / NV</th><th>Nội dung</th><th>Ghi chú</th>
              </tr></thead>
              <tbody>{badRows.map(r => (
                <tr key={r.key}>
                  <td className="tnum muted">{r.day}</td>
                  <td><Tag tone={r.source === 'Google' ? 'blue' : 'cyan'}>{r.source}</Tag></td>
                  <td><b>{r.store}</b></td>
                  <td className="right tnum">{r.star}</td>
                  <td className="muted">{r.who}</td>
                  <td className="need">{r.comment || <span className="muted">(không có nội dung)</span>}</td>
                  <td><Tag tone={r.extraTone}>{r.extra}</Tag></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
